import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from '../components/Icon';
import { useDocumentHead } from '../lib/seo';

const LAST_UPDATED = '16 May 2026';

export default function PrivacyPage() {
  const navigate = useNavigate();

  useDocumentHead({
    title: 'Privacy policy · ParkProof',
    description:
      'How ParkProof collects, uses, and protects your personal data. Contact: contact@parkproof.app.',
    canonicalPath: '/privacy',
  });

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <article className="p-s5 flex flex-col gap-s5 legal-prose">
        <header className="flex flex-col gap-s2">
          <div className="caption text-ink3 font-mono">LEGAL</div>
          <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
            Privacy <span className="font-serif-italic">policy</span>
          </h1>
          <p className="font-mono text-[11px] text-ink3 tracking-wide">
            Last updated {LAST_UPDATED}
          </p>
        </header>

        <Section title="1. Who runs ParkProof">
          <p>
            ParkProof is operated as a sole proprietorship (Einzelunternehmen) by:
          </p>
          <p className="font-medium text-ink1">
            Florian Le Rouzic
            <br />
            Stauffacherstrasse 179
            <br />
            8004 Zürich
            <br />
            Switzerland
          </p>
          <p>
            UID: CHE-{/* TODO: fill in UID-CHE number once registered */}TBD
            <br />
            Contact: <a className="underline" href="mailto:contact@parkproof.app">contact@parkproof.app</a>
          </p>
          <p>
            Florian Le Rouzic is the data controller responsible for the personal
            data processed through ParkProof under the Swiss Federal Act on Data
            Protection (FADP / revFADP) and, where applicable, the EU General Data
            Protection Regulation (GDPR).
          </p>
        </Section>

        <Section title="2. What data we collect">
          <p>We only collect what we need to run the service.</p>
          <ul>
            <li>
              <strong>Account data:</strong> your email address and, if you sign in
              with Google or Apple, the unique identifier and display name returned
              by that provider.
            </li>
            <li>
              <strong>Garage data:</strong> the make, model, year, and height of any
              cars you add to your garage, plus your roof-rack / roof-box settings.
            </li>
            <li>
              <strong>Verifications and reports:</strong> if you submit a clearance
              height for a parking garage, we store the height, an optional note,
              and the date / your user ID alongside it. These submissions are public.
            </li>
            <li>
              <strong>Approximate location:</strong> when you use the map or search,
              we may use your browser- or OS-provided location to show nearby
              garages. We do not store location traces.
            </li>
            <li>
              <strong>Technical data:</strong> IP address, browser / device type,
              and basic usage events needed to operate, secure, and improve the
              service (for example, to detect abuse or fix bugs).
            </li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal data, and we do not run
            third-party advertising trackers.
          </p>
        </Section>

        <Section title="3. Why we process this data (legal basis)">
          <ul>
            <li>
              <strong>To provide the service</strong> — performance of the contract
              you enter into when you create an account or use the app
              (FADP / GDPR Art. 6(1)(b)).
            </li>
            <li>
              <strong>To keep the service safe and improve it</strong> — our
              legitimate interest in running a reliable, secure product
              (GDPR Art. 6(1)(f)).
            </li>
            <li>
              <strong>To comply with legal obligations</strong>, for example
              responding to lawful requests from authorities.
            </li>
          </ul>
        </Section>

        <Section title="4. Service providers we use">
          <p>
            We rely on a small number of trusted processors. They only process your
            data on our instructions.
          </p>
          <ul>
            <li>
              <strong>Google Firebase</strong> (Google Ireland Ltd. / Google LLC) —
              authentication, database, hosting, and cloud functions.
            </li>
            <li>
              <strong>Google Maps Platform</strong> — map tiles, geocoding, and
              place data.
            </li>
            <li>
              <strong>Apple Inc.</strong> — Sign in with Apple, if you choose that
              option.
            </li>
          </ul>
          <p>
            Some of these providers are based in the United States. Where personal
            data is transferred outside Switzerland or the EEA, we rely on the
            European Commission's Standard Contractual Clauses and the Swiss FDPIC's
            recognised adequacy mechanisms.
          </p>
        </Section>

        <Section title="5. How long we keep your data">
          <ul>
            <li>
              <strong>Account & garage data:</strong> as long as your account is
              active. We delete it within 30 days of you closing your account.
            </li>
            <li>
              <strong>Public verifications:</strong> retained as part of the public
              parking record after account deletion, but de-identified from your
              user ID.
            </li>
            <li>
              <strong>Server logs:</strong> typically up to 90 days.
            </li>
          </ul>
        </Section>

        <Section title="6. Your rights">
          <p>You have the right to:</p>
          <ul>
            <li>access the personal data we hold about you;</li>
            <li>have inaccurate data corrected;</li>
            <li>have your data deleted (subject to legal obligations);</li>
            <li>object to or restrict certain processing;</li>
            <li>receive a copy of your data in a portable format;</li>
            <li>
              withdraw any consent you have given (this does not affect prior
              lawful processing).
            </li>
          </ul>
          <p>
            To exercise these rights, email{' '}
            <a className="underline" href="mailto:contact@parkproof.app">
              contact@parkproof.app
            </a>
            . If you believe we are mishandling your data you can also complain to
            the Swiss Federal Data Protection and Information Commissioner (FDPIC)
            or to your local supervisory authority in the EEA.
          </p>
        </Section>

        <Section title="7. Security">
          <p>
            We use industry-standard measures to protect your data, including
            encryption in transit (HTTPS), encrypted storage with our infrastructure
            providers, and least-privilege access controls. No system is perfectly
            secure — please use a strong, unique password.
          </p>
        </Section>

        <Section title="8. Children">
          <p>
            ParkProof is not directed at children under 13. If you believe a child
            has created an account, please contact us and we will delete it.
          </p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>
            We may update this policy as the service evolves. The "Last updated"
            date above will reflect any changes. For material changes we will give
            notice in the app or by email before they take effect.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Questions or requests:{' '}
            <a className="underline" href="mailto:contact@parkproof.app">
              contact@parkproof.app
            </a>
          </p>
        </Section>
      </article>
    </div>
  );
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-s2">
    <h2 className="font-serif text-[20px] leading-[24px] text-ink1">{title}</h2>
    <div className="font-sans text-[15px] leading-[24px] text-ink2 flex flex-col gap-s3">
      {children}
    </div>
  </section>
);
